import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonIcon, ButtonText, useToast } from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { faUsers, faUser as solidUser } from '@fortawesome/pro-solid-svg-icons';
import { useKeycloak } from '../AuthHOC';
import ProxyUserModal from './ProxyUserModal';
import { fetchAuthorProfileData, impersonateUser } from 'src/api/queries';
import { IAuthor } from '@publishing-engine/domain/types/AuthorType';
import { faPersonToPortal } from '@fortawesome/pro-light-svg-icons';
import {
  clearProxySession,
  getProxySession,
  storeProxySession
} from './storage';
import { useSubmissionId } from 'src/hooks/useSubmissionId';
import { useWorkspace } from 'src/pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import { useQuery } from '@tanstack/react-query';
import { faUserSlash } from '@fortawesome/pro-solid-svg-icons';

export type ProxyUserType = {
  user: {
    userId: string;
    firstName: string;
    lastName: string;
    isUserDeactivated?: boolean;
  };
  isDecisionMaker?: boolean;
  isButtonDisabled?: boolean;
  isButton?: boolean;
  role?: string;
  reviewId?: string;
  authorData?: IAuthor | null;
};

export const checkIsRoleAuthorOrReviewer = (roles: string[]) => {
  const hasEditor = roles.includes('editor');
  const hasReviewerOrAuthor =
    roles.includes('reviewer') ||
    roles.includes('author') ||
    roles.includes('board-member');

  return !hasEditor && hasReviewerOrAuthor;
};

export const ProxyBanner = ({
  showExitButton
}: {
  showExitButton?: boolean;
}) => {
  const [showModal, setShowModal] = useState(false);
  const { currentUser, keycloak, logout } = useKeycloak();
  const impersonator = keycloak?.tokenParsed?.IMPERSONATOR_USERNAME;

  useEffect(() => {
    if (!impersonator) {
      clearProxySession();
    }
  }, [impersonator]);

  const onClose = () => {
    setShowModal(false);
  };

  const handleExitProxy = () => {
    setShowModal(false);
    const returnTo = getProxySession()?.returnUrl ?? window.location.href;
    clearProxySession();
    logout(returnTo);
  };

  return (
    impersonator && (
      <>
        {showModal && (
          <ProxyUserModal
            isOpen={showModal}
            onClose={onClose}
            modalTitle={'Exit Proxy Mode?'}
            modalSubtitle={'You are about to stop acting as:'}
            modalDescription={
              'You will return to your account on the page where proxy mode was started.'
            }
            buttonText={'Exit Proxy Mode  '}
            handleProceed={handleExitProxy}
            user={currentUser}
          />
        )}

        {showExitButton ? (
          <ButtonText variant="primary" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faPersonToPortal} />
            Exit Proxy
          </ButtonText>
        ) : (
          <Flex
            width={'100%'}
            justifyContent={'center'}
            alignItems={'center'}
            gap={3}
            flexDirection={{ base: 'column', md: 'row' }}
            textAlign={{ base: 'center', md: 'left' }}
          >
            <FontAwesomeIcon icon={solidUser} />
            <Flex flexDirection={{ base: 'column', md: 'row' }} gap={1}>
              <Text textStyle={'headingLine'}>
                You are currently acting as: {currentUser.firstName}{' '}
                {currentUser.lastName}.
              </Text>
              <Text textStyle={'textBody'}>
                All actions will be performed on behalf of this user.
              </Text>
            </Flex>
            <Button variant="caution" onClick={() => setShowModal(true)}>
              Exit Proxy
            </Button>
          </Flex>
        )}
      </>
    )
  );
};

const ProxyUser = ({
  user,
  isDecisionMaker,
  isButtonDisabled,
  isButton,
  role,
  reviewId,
  authorData
}: ProxyUserType) => {
  const { currentUser, keycloak } = useKeycloak();
  const submissionId = useSubmissionId();
  const { orgId } = useWorkspace();

  const isProxySession = keycloak?.tokenParsed?.IMPERSONATOR_USERNAME;
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const id = user?.userId ?? '';
  const isLoggedInUser = currentUser?.id === id;
  const isUserDeactivated = user?.isUserDeactivated ?? false;

  const { data: fetchedAuthorData } = useQuery({
    queryKey: ['workspace-author-profile', orgId, id],
    queryFn: () => fetchAuthorProfileData(orgId, id),
    enabled: Boolean(orgId && id && !authorData)
  });

  const resolvedAuthorData = authorData ?? fetchedAuthorData;
  const isActiveUser = !resolvedAuthorData?.isProxyUser;

  useEffect(() => {
    if (!isProxySession) {
      clearProxySession();
    }
  }, [isProxySession]);

  const onClose = () => setShowModal(false);

  const handleProxyAsUser = async () => {
    if (!keycloak || !resolvedAuthorData) return;

    setIsLoading(true);

    const currentRealm = keycloak.realm || 'morressier';

    try {
      const isAuthorOrReviewer = role === 'author' || role === 'reviewer';

      const currentUrl =
        isAuthorOrReviewer && resolvedAuthorData?.isWorkspaceMember
          ? `${window.location.origin}/app/home`
          : window.location.href;

      storeProxySession({
        returnUrl: currentUrl,
        role,
        reviewId,
        submissionId: submissionId ?? ''
      });

      const response = await impersonateUser(id, currentRealm);

      if (response) {
        // Redirect browser to Keycloak to start impersonation flow
        window.location.href = currentUrl;
      }
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Proxy User',
        description:
          'An error occurred while proxying the user. Please sign in again and try.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => setShowModal(true);

  const isProxyUserAllowed =
    isButtonDisabled || isProxySession || isLoggedInUser || isActiveUser;

  return (
    <>
      {isUserDeactivated ? (
          <ButtonIcon
            icon={<FontAwesomeIcon icon={faUserSlash} style={{ color: 'red' }} />}
            size="small"
            variant="primary"
            ariaLabel="User deactivated"
          />
      ) : isDecisionMaker ? (
        <ButtonIcon
          icon={<FontAwesomeIcon icon={faUsers} />}
          size="small"
          variant="primary"
          onClick={openModal}
          disabled={isProxyUserAllowed}
          ariaLabel="Proxy"
        />
      ) : isButton ? (
        <Button width="fit-content" variant="secondary" onClick={openModal}>
          Proxy as User
        </Button>
      ) : (
        <Text onClick={openModal} textStyle="context">
          Proxy as User
        </Text>
      )}

      <ProxyUserModal
        isOpen={showModal}
        onClose={onClose}
        modalTitle={'Enter Proxy Mode'}
        modalSubtitle="You are about to act as:"
        modalDescription={
          'All actions you take will be performed on behalf of this user. Sensitive data (e.g., passwords) will remain hidden.'
        }
        buttonText={'Proceed'}
        handleProceed={handleProxyAsUser}
        user={user}
        isLoading={isLoading}
      />
    </>
  );
};

export default ProxyUser;
